import { useState, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput, FormTextarea, FormCheckbox } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { PencilIcon, TrashIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface BankingInfo {
  bank_name: string | null; bank_address: string | null; account_name: string | null
  account: string | null; routing: string | null; swift_code: string | null
}

interface CompanyRow {
  id: number; name: string; ein: string | null; address: string | null
  is_payment_option: boolean; front: boolean; banking_info: BankingInfo | Record<string, never>
}

interface Props { companies: CompanyRow[] }

const PAGE_SIZES = [30, 50, 100, 200]

const emptyForm = {
  name: '', ein: '', address: '', is_payment_option: false, front: false,
  bank_name: '', bank_address: '', account_name: '', account: '', routing: '', swift_code: '',
}

// ─── Component ──────────────────────────────────

export default function CompaniesIndex({ companies: initial }: Props) {
  const [companies, setCompanies] = useState(initial)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CompanyRow | null>(null)
  const [form, setForm] = useState({ ...emptyForm })
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow, flash } = useFlash()
  const csrfToken = typeof document !== 'undefined' ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '' : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])
  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(companies)

  const set = (key: string, val: string | boolean) => setForm(prev => ({ ...prev, [key]: val }))

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); setFormErrors([]); setDialogOpen(true) }
  const openEdit = (c: CompanyRow) => {
    const bi = c.banking_info || {}
    setEditing(c)
    setForm({
      name: c.name, ein: c.ein || '', address: c.address || '',
      is_payment_option: c.is_payment_option || false, front: c.front || false,
      bank_name: ('bank_name' in bi ? bi.bank_name : '') || '',
      bank_address: ('bank_address' in bi ? bi.bank_address : '') || '',
      account_name: ('account_name' in bi ? bi.account_name : '') || '',
      account: ('account' in bi ? bi.account : '') || '',
      routing: ('routing' in bi ? bi.routing : '') || '',
      swift_code: ('swift_code' in bi ? bi.swift_code : '') || '',
    })
    setFormErrors([])
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    const errors: string[] = []
    if (!form.name.trim()) errors.push('Name is required')
    if (!form.ein.trim()) errors.push('EIN is required')
    if (!form.address.trim()) errors.push('Address is required')
    if (errors.length) { setFormErrors(errors); return }
    setFormErrors([]); setSubmitting(true)
    try {
      const url = editing ? `/inertia_companies/${editing.id}.json` : '/inertia_companies.json'
      const method = editing ? 'PATCH' : 'POST'
      const payload = {
        company: {
          name: form.name.trim(), ein: form.ein.trim(), address: form.address.trim(),
          is_payment_option: form.is_payment_option, front: form.front,
          banking_information_attributes: {
            bank_name: form.bank_name.trim(), bank_address: form.bank_address.trim(),
            account_name: form.account_name.trim(), account: form.account.trim(),
            routing: form.routing.trim(), swift_code: form.swift_code.trim(),
          },
        },
      }
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload) })
      if (res.ok) {
        const data = await res.json()
        if (editing) { setCompanies(prev => prev.map(c => c.id === data.id ? data : c)); flash(data.id, 'success') }
        else { setCompanies(prev => [data, ...prev]) }
        setDialogOpen(false); notify(editing ? 'Saved:Company updated' : 'Saved:Company created')
      } else { const err = await res.json().catch(() => ({})); setFormErrors(err.errors || ['Failed to save']) }
    } catch { setFormErrors(['Network error']) }
    setSubmitting(false)
  }

  const deleteCompany = useCallback(async (id: number) => {
    if (!confirm('Delete this company?')) return
    try {
      const res = await fetch(`/inertia_companies/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setCompanies(prev => prev.filter(c => c.id !== id)); notify('Deleted:Company removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  const columns: DataTableColumn<CompanyRow>[] = [
    { key: 'name', label: 'Name', render: row => <span className="font-medium">{row.name}</span>, sortValue: row => row.name, filterValue: row => row.name },
    { key: 'ein', label: 'EIN', render: row => <>{row.ein || '—'}</>, sortValue: row => row.ein || '', filterValue: row => row.ein || '' },
    { key: 'address', label: 'Address', render: row => <span className="max-w-[200px] truncate block" title={row.address || ''}>{row.address || '—'}</span>, sortValue: row => row.address || '', filterValue: row => row.address || '' },
    { key: 'is_payment_option', label: 'Payment Option', render: row => <Badge color={row.is_payment_option ? 'emerald' : 'zinc'}>{row.is_payment_option ? 'Yes' : 'No'}</Badge>, sortValue: row => row.is_payment_option ? 'yes' : 'no', filterValue: row => row.is_payment_option ? 'Yes' : 'No' },
    { key: 'front', label: 'Front', render: row => <Badge color={row.front ? 'sky' : 'zinc'}>{row.front ? 'Yes' : 'No'}</Badge>, sortValue: row => row.front ? 'yes' : 'no', filterValue: row => row.front ? 'Yes' : 'No' },
  ]

  return (
    <>
      <Head title="Companies" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <PageHeader title="Companies" icon={<BuildingOffice2Icon className="h-4 w-4" />} actions={
        <div className="flex items-center gap-2">
          <div className="w-36"><FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} /></div>
          <Button onClick={openCreate}>+ New Company</Button>
        </div>
      } />
      <div className="mb-2"><Badge color="zinc"><span className="text-xs font-medium">{totalCount} companies</span></Badge></div>

      <DataTable rows={paginatedRows} columns={columns} rowKey={row => row.id} flashRowId={flashRow?.id ?? null} flashStatus={flashRow?.status ?? null} emptyText="No companies" filteredEmptyText="No companies match your filters"
        actions={row => (
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => openEdit(row)} className="rounded p-1 text-zinc-400 hover:text-indigo-600 dark:text-zinc-500 dark:hover:text-indigo-400" title="Edit"><PencilIcon className="h-4 w-4" /></button>
            <button onClick={() => deleteCompany(row.id)} className="rounded p-1 text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400" title="Delete"><TrashIcon className="h-4 w-4" /></button>
          </div>
        )} />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editing ? 'Edit Company' : 'New Company'}</DialogTitle>
        <DialogBody>
          {formErrors.length > 0 && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div className="space-y-4">
            {/* ── Company Info ── */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-zinc-800 dark:text-zinc-200">Company Info</h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Name *</label>
                  <FormInput value={form.name} onChange={e => set('name', (e.target as HTMLInputElement).value)} placeholder="Company name..." autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">EIN *</label>
                    <FormInput value={form.ein} onChange={e => set('ein', (e.target as HTMLInputElement).value)} placeholder="EIN..." />
                  </div>
                  <div className="flex items-end gap-4 pb-1">
                    <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 dark:text-zinc-300">
                      <FormCheckbox checked={form.is_payment_option} onChange={v => set('is_payment_option', v)} />
                      Payment Option
                    </label>
                    <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 dark:text-zinc-300">
                      <FormCheckbox checked={form.front} onChange={v => set('front', v)} />
                      Front
                    </label>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Address *</label>
                  <FormTextarea value={form.address} onChange={e => set('address', (e.target as HTMLTextAreaElement).value)} rows={2} placeholder="Company address..." />
                </div>
              </div>
            </div>

            {/* ── Banking Info ── */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-zinc-800 dark:text-zinc-200">Banking Info</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Bank Name</label>
                    <FormInput value={form.bank_name} onChange={e => set('bank_name', (e.target as HTMLInputElement).value)} placeholder="Bank name..." />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Bank Address</label>
                    <FormInput value={form.bank_address} onChange={e => set('bank_address', (e.target as HTMLInputElement).value)} placeholder="Bank address..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Account Name</label>
                    <FormInput value={form.account_name} onChange={e => set('account_name', (e.target as HTMLInputElement).value)} placeholder="Account name..." />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Account #</label>
                    <FormInput value={form.account} onChange={e => set('account', (e.target as HTMLInputElement).value)} placeholder="Account number..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Routing #</label>
                    <FormInput value={form.routing} onChange={e => set('routing', (e.target as HTMLInputElement).value)} placeholder="Routing number..." />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">SWIFT Code</label>
                    <FormInput value={form.swift_code} onChange={e => set('swift_code', (e.target as HTMLInputElement).value)} placeholder="SWIFT code..." />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button disabled={submitting} onClick={handleSubmit}>{submitting ? 'Saving...' : (editing ? 'Save' : 'Create')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

CompaniesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
